import {
  BellRing,
  BookOpenCheck,
  FileSearch,
  FileText,
  Forward,
  Headphones,
  ListChecks,
  ListTodo,
  MessageCircleMore,
  MessageSquareText,
  MonitorSmartphone,
  Paperclip,
  PhoneCall,
  Radio,
  Search,
  Sparkles,
  StickyNote,
  TicketCheck,
  UserCheck,
  UsersRound,
  Video,
} from "lucide-react";

export const productGroups = [
  {
    title: "Products",
    products: ["messaging","ticketing","remote-access"],
  },
];

export const products = {
  messaging: {
    slug: "messaging",
    name: "Connect",
    menuDescription: "Direct and group conversations with context intact.",
    badge: "WORKSPACE",
    icon: MessageSquareText,
    eyebrow: "CULLATH MESSAGING",
    title: "Keep every conversation in orbit.",
    heroCopy:
      "Bring direct messages, group conversations, files, search and live presence into one focused workspace so teams can move without losing the thread.",
    summary: ["Direct + group chat", "Presence + typing states", "Files, search + forwarding"],
    visual: "messaging",
    features: [
      { icon: MessageCircleMore, title: "Direct and group chat", copy: "Move between one-to-one conversations and team discussions without rebuilding context." },
      { icon: UserCheck, title: "Live presence", copy: "See availability and typing states so conversations feel immediate and coordinated." },
      { icon: Paperclip, title: "Share files in context", copy: "Keep attachments beside the message and people that gave the file meaning." },
      { icon: Search, title: "Search conversations", copy: "Find messages and details without manually scrolling through long threads." },
      { icon: Forward, title: "Forward important messages", copy: "Move useful context into another conversation when the next team needs it." },
      { icon: FileText, title: "Draft before you send", copy: "Keep unfinished thoughts available while you switch between work conversations." },
    ],
    steps: [
      { number: "01", title: "Start the conversation", copy: "Message a teammate or open a group discussion around the work." },
      { number: "02", title: "Add the context", copy: "Share files, search earlier messages and bring the right people into the thread." },
      { number: "03", title: "Move to the next action", copy: "Jump to a call or turn a request into trackable work without starting over." },
    ],
    connected: ["calls", "ticketing", "my-space"],
  },
  calls: {
    slug: "calls",
    name: "Calls & screen sharing",
    shortName: "Calls",
    menuDescription: "Voice, video and screen sharing beside chat.",
    badge: "WORKSPACE",
    icon: Video,
    eyebrow: "CULLATH CALLS",
    title: "Move from typing to talking in seconds.",
    heroCopy:
      "Start voice or video conversations and share your screen from the same workspace where the discussion began—without reconstructing the problem in another tool.",
    summary: ["Voice calling", "Video conversations", "Screen sharing"],
    visual: "calls",
    features: [
      { icon: PhoneCall, title: "Voice calls", copy: "Turn a long message thread into a direct live conversation when speed matters." },
      { icon: Video, title: "Video conversations", copy: "Meet face to face from the same collaboration environment your team already uses." },
      { icon: MonitorSmartphone, title: "Screen sharing", copy: "Present the exact screen or workflow being discussed so everyone sees the same issue." },
      { icon: UsersRound, title: "Group call flows", copy: "Bring multiple teammates into live conversations for shared reviews and decisions." },
      { icon: Radio, title: "In-app call controls", copy: "Manage the live session without forcing users through a disconnected experience." },
      { icon: MessageSquareText, title: "Conversation stays close", copy: "Return to the surrounding message context after the call instead of losing the trail." },
    ],
    steps: [
      { number: "01", title: "Discuss in chat", copy: "Start where the work already lives: inside the conversation." },
      { number: "02", title: "Start the call", copy: "Move into voice or video when a live discussion is the faster route." },
      { number: "03", title: "Share and decide", copy: "Present the screen, align on the next step and return to the same work context." },
    ],
    connected: ["messaging", "transcripts", "remote-access"],
  },
  ticketing: {
    slug: "ticketing",
    name: "Desk",
    menuDescription: "Assign, prioritize and track operational requests.",
    badge: "OPERATIONS",
    icon: TicketCheck,
    eyebrow: "CULLATH TICKETING",
    title: "Turn requests into visible ownership.",
    heroCopy:
      "Create individual or team tickets, assign responsibility, set priority and follow live activity so important requests do not disappear inside conversation history.",
    summary: ["Team + individual tickets", "Priority + status", "Live activity updates"],
    visual: "ticketing",
    features: [
      { icon: TicketCheck, title: "Create tickets quickly", copy: "Give an operational request a trackable home instead of leaving it buried in chat." },
      { icon: UsersRound, title: "Team and individual flows", copy: "Route work to a team or a specific person depending on who owns the next action." },
      { icon: UserCheck, title: "Clear assignment", copy: "Make ownership visible so teams know who is responsible for moving the request." },
      { icon: ListChecks, title: "Priority and status", copy: "Separate urgent work from routine items and keep the current state easy to understand." },
      { icon: BellRing, title: "Unread awareness", copy: "Surface ticket activity that still needs attention instead of relying on memory." },
      { icon: Radio, title: "Live updates", copy: "Keep the latest ticket activity visible as ownership and status change." },
    ],
    steps: [
      { number: "01", title: "Capture the request", copy: "Create a ticket when a message, issue or task needs accountable follow-through." },
      { number: "02", title: "Assign and prioritize", copy: "Choose the responsible destination and make urgency visible." },
      { number: "03", title: "Track it to closure", copy: "Follow status and live activity until the request reaches its next outcome." },
    ],
    connected: ["messaging", "transcripts", "remote-access"],
  },
  "remote-access": {
    slug: "remote-access",
    name: "Assist",
    menuDescription: "Support teammates from the conversation that started it.",
    badge: "OPERATIONS · BETA",
    icon: MonitorSmartphone,
    eyebrow: "CULLATH REMOTE ACCESS · BETA",
    title: "Bring support closer to the screen.",
    heroCopy:
      "Use built-in remote access request and control flows to reduce the distance between a reported issue and the teammate trying to understand or resolve it.",
    summary: ["Connection requests", "Remote control flows", "Support beside conversation"],
    visual: "remote",
    features: [
      { icon: MonitorSmartphone, title: "Remote connection flow", copy: "Start support from within the Cullath environment instead of asking users to find another tool." },
      { icon: UserCheck, title: "Request-based access", copy: "Use an explicit request and accept flow before a remote support session begins." },
      { icon: Radio, title: "Connection state", copy: "Keep the active remote session visible while support is in progress." },
      { icon: MessageSquareText, title: "Conversation nearby", copy: "Keep the original support discussion close to the screen being reviewed." },
      { icon: TicketCheck, title: "Operational follow-up", copy: "Move unresolved work into ticketing when the issue needs additional ownership." },
      { icon: PhoneCall, title: "Live support workflow", copy: "Pair remote assistance with team communication when the user needs explanation as well as action." },
    ],
    steps: [
      { number: "01", title: "Receive the issue", copy: "The support moment begins in a message or operational request." },
      { number: "02", title: "Request a session", copy: "Start the remote access flow and connect after the user accepts." },
      { number: "03", title: "Resolve or follow up", copy: "Assist directly, then create trackable follow-up work when more action is required." },
    ],
    connected: ["messaging", "calls", "ticketing"],
  },
  "my-space": {
    slug: "my-space",
    name: "My Space",
    menuDescription: "Notes, bookmarks, reminders and priorities in one view.",
    badge: "PERSONAL WORK",
    icon: Sparkles,
    eyebrow: "CULLATH MY SPACE",
    title: "Give every thought somewhere useful to land.",
    heroCopy:
      "Keep reminders, sticky notes, bookmarks and priority to-dos in one focused personal area between conversation and execution.",
    summary: ["Priority to-dos", "Sticky notes", "Bookmarks + reminders"],
    visual: "space",
    features: [
      { icon: ListTodo, title: "Priority to-dos", copy: "Keep the next actions visible and distinguish higher-priority work from routine items." },
      { icon: StickyNote, title: "Sticky notes", copy: "Capture lightweight thoughts and working notes without leaving the workspace." },
      { icon: BookOpenCheck, title: "Bookmarks", copy: "Save useful conversation context so important details are easier to return to." },
      { icon: BellRing, title: "Reminders", copy: "Give time-sensitive follow-up a place to resurface when you need it." },
      { icon: Sparkles, title: "One focused panel", copy: "See personal work tools together instead of spreading small tasks across separate apps." },
      { icon: MessageSquareText, title: "Close to collaboration", copy: "Move useful details from conversation into personal follow-up while the context is still fresh." },
    ],
    steps: [
      { number: "01", title: "Capture it", copy: "Save a thought, message or follow-up the moment it becomes important." },
      { number: "02", title: "Give it shape", copy: "Turn it into a note, bookmark, reminder or prioritized to-do." },
      { number: "03", title: "Return with focus", copy: "Use My Space as a compact view of personal work that still needs attention." },
    ],
    connected: ["messaging", "ticketing", "transcripts"],
  },
  transcripts: {
    slug: "transcripts",
    name: "Audio Transcripts",
    shortName: "Transcripts",
    menuDescription: "Move audio into readable follow-up and ticket workflows.",
    badge: "PERSONAL WORK",
    icon: Headphones,
    eyebrow: "CULLATH AUDIO TRANSCRIPTS",
    title: "Move from audio to structured follow-up.",
    heroCopy:
      "Upload audio, review transcript information and move the resulting follow-up into a ticket when the conversation needs accountable action.",
    summary: ["Audio upload flow", "Transcript review", "Create ticket from follow-up"],
    visual: "transcripts",
    features: [
      { icon: Headphones, title: "Audio workflow", copy: "Bring audio into a dedicated Cullath flow rather than treating it as an isolated file." },
      { icon: FileText, title: "Readable transcript data", copy: "Review conversation information in a text-based format that is easier to scan." },
      { icon: FileSearch, title: "Review before action", copy: "Use the transcript workflow to understand what needs follow-up before creating work." },
      { icon: TicketCheck, title: "Create a ticket", copy: "Move transcript follow-up into a ticket when ownership and status tracking are needed." },
      { icon: ListChecks, title: "Structured next steps", copy: "Reduce the gap between listening to audio and deciding what happens next." },
      { icon: MessageSquareText, title: "Connected workspace", copy: "Keep the follow-up process inside the broader communication and operations environment." },
    ],
    steps: [
      { number: "01", title: "Bring in the audio", copy: "Upload the recording into the transcript workflow." },
      { number: "02", title: "Review the transcript", copy: "Read the resulting information and identify the required follow-up." },
      { number: "03", title: "Create accountable work", copy: "Turn the follow-up into a ticket when another person or team needs to own it." },
    ],
    connected: ["ticketing", "my-space", "messaging"],
  },
};

export const allProducts = Object.values(products);

export function getProduct(slug) {
  return products[slug] || null;
}

export function productHref(slug) {
  const routes = {
    messaging: "/connect",
    ticketing: "/desk",
    "remote-access": "/assist",
    "my-space": "/my-space",
  };

  return routes[slug] || "/";
}
